<script setup lang="ts">
import {
  type Interval,
  eachMinuteOfInterval,
  isSameDay,
  isWithinInterval,
  parse,
} from 'date-fns';
import SlotTimepickerSlots from './SlotTimepickerSlots.vue';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue?: Date;
    date?: Date;
    loading?: boolean;
    whitelistedIntervals?: Interval[];
    step?: number;
    maxSlots?: number;
    groupByDaytime?: boolean;
  }>(),
  {
    modelValue: undefined,
    date: () => new Date(),
    loading: false,
    whitelistedIntervals: () => [],
    step: 15,
    maxSlots: 10,
    groupByDaytime: false,
  }
);
const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: Date): void;
}>();

function dateFromHour(hour: number) {
  return parse(`${hour}:0:0`, 'H:m:s', new Date('2024-03-26'));
}

type DayTimeGroup = {
  name: string;
  interval: Interval;
};
const dayTimeGroups: DayTimeGroup[] = [
  {
    name: 'Ночь',
    interval: {
      start: dateFromHour(0),
      end: dateFromHour(6),
    },
  },
  {
    name: 'Утро',
    interval: {
      start: dateFromHour(6),
      end: dateFromHour(12),
    },
  },
  {
    name: 'День',
    interval: {
      start: dateFromHour(12),
      end: dateFromHour(18),
    },
  },
  {
    name: 'Вечер',
    interval: {
      start: dateFromHour(18),
      end: dateFromHour(23),
    },
  },
];

const timeSlots = computed(() => {
  const slots: Date[] = [];
  for (const period of props.whitelistedIntervals) {
    if (!isSameDay(period.start, props.date)) continue;
    slots.push(...eachMinuteOfInterval(period, { step: props.step }));
  }
  return slots;
});

type TimeSlotGroup = {
  name: string;
  slots: Date[];
};
const timeSlotGroups = computed<TimeSlotGroup[]>(() => {
  if (!props.groupByDaytime) return [{ name: '', slots: timeSlots.value }];
  return dayTimeGroups.map((group) => ({
    name: group.name,
    slots: timeSlots.value.filter((slot) =>
      isWithinInterval(slot, group.interval)
    ),
  }));
});

function updateModelValue(value: Date) {
  emit('update:modelValue', value);
}
</script>

<template>
  <AppLoader v-if="props.loading" />
  <div v-else-if="timeSlots.length > 0">
    <div v-for="group in timeSlotGroups" :key="group.name">
      <div :class="$style.groupName">
        {{ group.name }}
      </div>
      <SlotTimepickerSlots
        :slots="group.slots"
        :max-slots="props.maxSlots"
        :model-value="props.modelValue"
        @update:model-value="updateModelValue"
      />
    </div>
  </div>
</template>

<style module>
.groupName {
  font-weight: 700;
  margin-bottom: 12px;
}
</style>
